/**
 * Site service for client-side API calls
 */
import apiService from './apiService';
import fallbackService from './fallbackService';

// Flag to control whether to use fallback service
const USE_FALLBACK = false;

const siteService = {
  /**
   * Get all sites
   * @returns {Promise<Array>} - Array of sites
   */
  async fetchSites() {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback site service');
        return fallbackService.sites.getAllSites();
      }
      
      return await apiService.get('/sites');
    } catch (error) {
      console.error('Get sites error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get site by ID
   * @param {number|string} id - Site ID
   * @returns {Promise<Object>} - Site data
   */
  async fetchSite(id) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback site service for site ${id}`);
        return fallbackService.sites.getSiteById(id);
      }
      
      return await apiService.get(`/sites/${id}`);
    } catch (error) {
      console.error(`Get site ${id} error:`, error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Create a new site
   * @param {Object} siteData - Site data
   * @returns {Promise<Object>} - Created site
   */
  async createSite(siteData) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback site service for creation');
        // For mock, just return the data with an ID
        return { 
          ...siteData, 
          id: Math.floor(Math.random() * 1000) + 100 
        };
      }
      
      return await apiService.post('/sites', siteData);
    } catch (error) {
      console.error('Create site error:', error);
      throw error;
    }
  },

  /**
   * Update a site
   * @param {number|string} id - Site ID
   * @param {Object} siteData - Updated site data
   * @returns {Promise<Object>} - Updated site
   */
  async updateSite(id, siteData) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback site service for updating site ${id}`);
        // For mock, just return the data
        return { 
          ...siteData, 
          id: parseInt(id)
        };
      }
      
      return await apiService.put(`/sites/${id}`, siteData);
    } catch (error) {
      console.error(`Update site ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Delete a site
   * @param {number|string} id - Site ID
   * @returns {Promise<Object>} - Response data
   */
  async deleteSite(id) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback site service for deleting site ${id}`);
        // For mock, just return success
        return { success: true };
      }
      
      await apiService.delete(`/sites/${id}`);
      // Return a success object since the server returns 204 No Content
      return { success: true, id };
    } catch (error) {
      console.error(`Delete site ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Get devices for a site
   * @param {number|string} id - Site ID
   * @returns {Promise<Array>} - Array of devices
   */
  async fetchSiteDevices(id) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback site service for site ${id} devices`);
        return fallbackService.sites.getSiteDevices(id);
      }
      
      return await apiService.get(`/sites/${id}/devices`);
    } catch (error) {
      console.error(`Get site ${id} devices error:`, error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get usage data for a site
   * @param {number|string} id - Site ID
   * @param {string} period - Period ('daily', 'monthly', 'yearly')
   * @returns {Promise<Array>} - Usage data
   */
  async fetchSiteUsage(id, period = 'daily') {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback site service for site ${id} usage`);
        return fallbackService.sites.getSiteUsage(id, period);
      }
      
      return await apiService.get(`/sites/${id}/usage?period=${period}`);
    } catch (error) {
      console.error(`Get site ${id} usage error:`, error);
      // Do not fall back to mock data
      throw error;
    }
  },

  async fetchSiteNotifications(id, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit != null) queryParams.append('limit', String(params.limit));
      if (params.offset != null) queryParams.append('offset', String(params.offset));
      if (params.status) queryParams.append('status', String(params.status));
      if (params.type) queryParams.append('type', String(params.type));
      if (Array.isArray(params.types) && params.types.length > 0) {
        queryParams.append(
          'types',
          params.types
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(',')
        );
      }
      if (params.includeTotal != null) queryParams.append('includeTotal', String(params.includeTotal));
      if (params.startDate) queryParams.append('startDate', String(params.startDate));
      if (params.endDate) queryParams.append('endDate', String(params.endDate));
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      return await apiService.get(`/sites/${id}/notifications${queryString}`);
    } catch (error) {
      console.error(`Get site ${id} notifications error:`, error);
      throw error;
    }
  },

  async updateSiteNotificationStatus(siteId, notificationId, status) {
    try {
      return await apiService.put(`/sites/${siteId}/notifications/${notificationId}/status`, { status });
    } catch (error) {
      console.error(`Update site ${siteId} notification ${notificationId} status error:`, error);
      throw error;
    }
  },

  async deleteSiteNotification(siteId, notificationId) {
    try {
      return await apiService.delete(`/sites/${siteId}/notifications/${notificationId}`);
    } catch (error) {
      console.error(`Delete site ${siteId} notification ${notificationId} error:`, error);
      throw error;
    }
  },

  /**
   * Update devices for a site
   * @param {number|string} siteId - Site ID
   * @param {Array<number|string>} deviceIds - Array of device IDs to link to the site
   * @returns {Promise<Object>} - Response data
   */
  async updateSiteDevices(siteId, deviceIds) {
    try {
      if (USE_FALLBACK) {
        console.log(`Using fallback service for updating site ${siteId} devices`);
        return { success: true, siteId, deviceIds };
      }
      
      return await apiService.put(`/sites/${siteId}/devices`, { deviceIds });
    } catch (error) {
      console.error(`Update site ${siteId} devices error:`, error);
      throw error;
    }
  },

  /**
   * Import sites from CSV file
   * @param {FormData} formData - FormData containing the CSV file
   * @returns {Promise<Object>} - Import result with count of imported sites
   */
  async importSitesFromCsv(formData) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback service for CSV import');
        return { imported: 3, skipped: 0, errors: [] };
      }
      
      // apiService now handles FormData properly, no need for custom headers
      return await apiService.post('/sites/import-csv', formData);
    } catch (error) {
      console.error('CSV import error:', error);
      throw error;
    }
  }
};

export default siteService;
